import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Modal } from '@telegram-apps/telegram-ui';
import { useAppRootContext } from '@telegram-apps/telegram-ui/dist/hooks/useAppRootContext';
import { post } from '../../helpers/api';
import styles from './RechargeModal.module.css';

const PRESET_AMOUNTS = [10, 30, 50, 100, 200, 500];
const PASSWORD_LENGTH = 6;

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  proxyId: number;
  proxyName: string;
  balance: number;
  onSuccess?: () => void;
};

type Toast = { type: 'success' | 'error'; text: string };

export const RechargeModal = ({ open, onOpenChange, proxyId, proxyName, balance, onSuccess }: Props) => {
  const { portalContainer } = useAppRootContext();
  const [selected, setSelected] = useState<number | null>(null);
  const [custom, setCustom] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [confirmName, setConfirmName] = useState('');
  const [confirmAmount, setConfirmAmount] = useState<number | null>(null);
  const [confirmProxyId, setConfirmProxyId] = useState(0);
  const [password, setPassword] = useState('');
  const [toast, setToast] = useState<Toast | null>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const toastTimer = useRef<number>();

  useEffect(() => {
    if (open) {
      setSelected(null);
      setCustom('');
      setError(null);
      setSubmitting(false);
      setConfirming(false);
      setPassword('');
    }
  }, [open]);

  useEffect(() => () => window.clearTimeout(toastTimer.current), []);

  useEffect(() => {
    if (confirming) {
      const timer = window.setTimeout(() => passwordRef.current?.focus(), 50);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [confirming]);

  const showToast = (next: Toast) => {
    window.clearTimeout(toastTimer.current);
    setToast(next);
    toastTimer.current = window.setTimeout(() => setToast(null), 2200);
  };

  const customValue = Number(custom);
  const amount = custom !== '' && customValue > 0 ? customValue : selected;

  const handleConfirm = async () => {
    if (!confirmAmount || confirmAmount <= 0 || submitting) {
      return;
    }
    if (password.length !== PASSWORD_LENGTH) {
      setError(`请输入 ${PASSWORD_LENGTH} 位数字密码`);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await post<number>('/admin/tel/recharge', {
        proxyId: confirmProxyId,
        amount: confirmAmount,
        password,
      });
      showToast({ type: 'success', text: `充值 ¥${confirmAmount.toLocaleString()} 成功` });
      setConfirming(false);
      setSelected(null);
      setCustom('');
      setPassword('');
      onSuccess?.();
      onOpenChange(false);
    } catch (e) {
      const text = e instanceof Error ? e.message : '充值失败';
      setError(text);
      showToast({ type: 'error', text });
      setSubmitting(false);
    }
  };

  return (
    <>
      <Modal
        open={open && !confirming}
        onOpenChange={onOpenChange}
        className={`app-modal ${styles.modal}`}
        header={
          <div className={styles.banner}>
            <div className={styles.bannerTitle}>{proxyName || `代理 #${proxyId}`}充值</div>
            <div className={styles.bannerSub}>
              当前余额 ¥
              {Number(balance || 0).toLocaleString('zh-CN', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
            <div className={styles.bannerBars} aria-hidden="true">
              <i /><i /><i />
            </div>
          </div>
        }
      >
        <div className={styles.body}>
          <div className={styles.sectionLabel}>选择充值金额</div>
          <div className={styles.options}>
            {PRESET_AMOUNTS.map((value) => (
              <button
                key={value}
                type="button"
                className={`${styles.option} ${selected === value && custom === '' ? styles.optionActive : ''}`}
                onClick={() => {
                  setSelected(value);
                  setCustom('');
                  setError(null);
                }}
              >
                ¥{value}
              </button>
            ))}
          </div>

          <div className={styles.custom}>
            <span className={styles.customSymbol}>¥</span>
            <input
              className={styles.customInput}
              type="number"
              inputMode="decimal"
              placeholder="自定义金额"
              value={custom}
              onChange={(e) => {
                setCustom(e.target.value);
                setSelected(null);
                setError(null);
              }}
            />
          </div>

          {error && <div className={styles.error}>{error}</div>}

          <button
            type="button"
            className={styles.submit}
            disabled={!amount || amount <= 0 || submitting}
            onClick={() => {
              setError(null);
              setConfirmName(proxyName || `代理 #${proxyId}`);
              setConfirmAmount(amount ?? null);
              setConfirmProxyId(proxyId);
              setConfirming(true);
            }}
          >
            确认充值
          </button>
        </div>
      </Modal>

      {confirming &&
        portalContainer?.current &&
        createPortal(
          <div className={styles.overlay} onClick={() => !submitting && setConfirming(false)}>
            <div className={styles.sheet} onClick={(e) => e.stopPropagation()}>
              <div className={styles.sheetBanner}>
                <div className={styles.sheetBannerTitle}>确认充值</div>
                <div className={styles.sheetBannerSub}>
                  确认为 {confirmName} 充值
                </div>
                <div className={styles.sheetBannerBars} aria-hidden="true">
                  <i /><i /><i />
                </div>
              </div>
              <div className={styles.sheetBody}>
                <div className={styles.confirmAmount}>¥{confirmAmount?.toLocaleString()}</div>
                <div
                  className={styles.passwordBoxes}
                  onClick={() => passwordRef.current?.focus()}
                >
                  {Array.from({ length: PASSWORD_LENGTH }).map((_, index) => (
                    <div
                      key={index}
                      className={`${styles.passwordCell} ${
                        index === password.length && password.length < PASSWORD_LENGTH
                          ? styles.passwordCellActive
                          : ''
                      }`}
                    >
                      {password[index] ? '•' : ''}
                    </div>
                  ))}
                  <input
                    ref={passwordRef}
                    className={styles.passwordHidden}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    name="recharge-code"
                    data-lpignore="true"
                    data-form-type="other"
                    maxLength={PASSWORD_LENGTH}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value.replace(/\D/g, '').slice(0, PASSWORD_LENGTH));
                      setError(null);
                    }}
                  />
                </div>
                <div className={styles.sheetDesc}>请输入 6 位充值密码以确认本次操作</div>
                {error && <div className={styles.confirmError}>{error}</div>}
                <div className={styles.confirmActions}>
                  <button
                    type="button"
                    className={styles.cancel}
                    disabled={submitting}
                    onClick={() => setConfirming(false)}
                  >
                    取消
                  </button>
                  <button
                    type="button"
                    className={styles.submit}
                    disabled={submitting}
                    onClick={handleConfirm}
                  >
                    {submitting ? '提交中…' : '确认充值'}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          portalContainer.current
        )}

      {toast &&
        portalContainer?.current &&
        createPortal(
          <div className={`${styles.toast} ${toast.type === 'success' ? styles.toastSuccess : styles.toastError}`}>
            {toast.text}
          </div>,
          portalContainer.current
        )}
    </>
  );
};
